import React, { useEffect, useRef, useState } from 'react';
import { formatFeedbackTime } from '../../lib/feedbackPosts/formatTime';
import { deleteFeedbackComment } from '../../lib/feedbackPosts/firestore';
import ConfirmDialog from './ConfirmDialog';

/**
 * Single feedback comment row with author-only three-dot menu and delete + confirmation.
 * @param {'compact'|'comfortable'} variant - layout density (feed expanded vs full page)
 */
export default function FeedbackCommentItem({
  comment: c,
  postId,
  currentUserId,
  onNavigate,
  variant = 'compact',
}) {
  const isAuthor = Boolean(currentUserId && c.authorId === currentUserId);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleDeleteConfirm = async () => {
    if (!currentUserId || c.authorId !== currentUserId || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deleteFeedbackComment({
        postId,
        commentId: c.id,
        currentUserId,
      });
      setConfirmOpen(false);
      setMenuOpen(false);
    } catch (err) {
      console.error('[FeedbackCommentItem] delete failed', err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const liClass =
    variant === 'comfortable'
      ? 'rounded-lg border border-slate-200 bg-white p-4 shadow-sm'
      : 'rounded-lg border border-slate-200 bg-white p-3 shadow-sm';

  const authorBlockCompact = c.authorUsername ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onNavigate('u/' + c.authorUsername);
      }}
      className="flex min-w-0 items-center gap-2 rounded-md -m-1 p-1 text-left hover:bg-slate-50"
      aria-label={`View ${c.authorDisplayName}'s profile`}
    >
      <span className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-slate-100">
        {c.authorPhotoURL ? (
          <img src={c.authorPhotoURL} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-xs font-semibold text-slate-500">
            {c.authorDisplayName?.charAt(0)}
          </span>
        )}
      </span>
      <span className="min-w-0 text-xs text-slate-600">
        <span className="font-semibold text-slate-900">{c.authorDisplayName}</span>
        <span className="text-slate-400"> · </span>
        <span className="text-slate-500">@{c.authorUsername}</span>
        <span className="text-slate-400"> · {formatFeedbackTime(c.createdAt)}</span>
      </span>
    </button>
  ) : (
    <span className="text-xs text-slate-600">
      {c.authorDisplayName} · {formatFeedbackTime(c.createdAt)}
    </span>
  );

  const authorBlockComfortable = c.authorUsername ? (
    <button
      type="button"
      onClick={() => onNavigate('u/' + c.authorUsername)}
      className="flex min-w-0 items-center gap-3 rounded-lg -m-1 p-1 text-left transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-ihTeal/50 focus:ring-inset"
      aria-label={`View ${c.authorDisplayName}'s profile`}
    >
      <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-full bg-slate-100">
        {c.authorPhotoURL ? (
          <img src={c.authorPhotoURL} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-slate-500">
            {c.authorDisplayName?.charAt(0)}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{c.authorDisplayName}</p>
        <p className="text-xs text-slate-500">
          @{c.authorUsername} · {formatFeedbackTime(c.createdAt)}
        </p>
      </div>
    </button>
  ) : (
    <div className="flex min-w-0 items-center gap-3">
      <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-full bg-slate-100">
        {c.authorPhotoURL ? (
          <img src={c.authorPhotoURL} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-slate-500">
            {c.authorDisplayName?.charAt(0)}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{c.authorDisplayName}</p>
        <p className="text-xs text-slate-500">@{c.authorUsername} · {formatFeedbackTime(c.createdAt)}</p>
      </div>
    </div>
  );

  const bodyClass =
    variant === 'comfortable'
      ? 'mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed'
      : 'mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed';

  return (
    <li className={liClass} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {variant === 'comfortable' ? authorBlockComfortable : authorBlockCompact}
        </div>
        {isAuthor && (
          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((prev) => !prev);
              }}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-ihTeal/30"
              aria-label="Comment actions"
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full z-20 mt-1 w-36 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    setConfirmOpen(true);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <p className={bodyClass}>{c.body}</p>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => !deleteBusy && setConfirmOpen(false)}
        title="Delete this comment?"
        body="This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        busy={deleteBusy}
        onConfirm={handleDeleteConfirm}
      />
    </li>
  );
}
